import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRoles } from "@/context/RolesContext";
import { useDialog } from "@/context/DialogContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

// Built-in role Tailwind fixed colors — custom roles fall back to their `color` field.
const BUILT_IN_ROLE_BG = {
  admin: "bg-[#002FA7] text-white",
  manager: "bg-[#FF3B30] text-white",
  hr: "bg-[#7C3AED] text-white",
  employee: "bg-[#0A0A0A] text-white",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const nav = useNavigate();
  const { user, setUser, refresh } = useAuth();
  const { roles: allRoles } = useRoles();
  const { confirm } = useDialog();
  const [form, setForm] = useState({ name: user.name, department: user.department || "", position: user.position || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [em, setEm] = useState({ password: "", new_email: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pwValid, setPwValid] = useState(false);
  const [signoutBusy, setSignoutBusy] = useState(false);

  const flash = (m) => { toast.success(m); };
  const showErr = (e) => { 
    const errorMsg = formatApiErrorDetail(e.response?.data?.detail);
    toast.error(errorMsg);
    setError(errorMsg);
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const { data } = await api.patch(`/users/${user.id}`, form);
      setUser(data); flash("Profile updated");
    } catch (err) { showErr(err); }
  };

  const changePw = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" }); flash("Password changed");
    } catch (err) { showErr(err); }
  };

  const signoutOthers = async () => {
    const confirmed = await confirm({
      title: "Sign Out Other Sessions",
      description: "Sign out all other devices/sessions? Your current session will stay signed in.",
      confirmText: "Sign Out",
      cancelText: "Cancel"
    });
    
    if (!confirmed) return;
    
    setSignoutBusy(true); setError("");
    try {
      await api.post("/auth/signout-others");
      flash("All other sessions signed out.");
    } catch (err) { showErr(err); }
    finally { setSignoutBusy(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/auth/change-email", em);
      setEm({ password: "", new_email: "" }); await refresh(); flash("Email updated");
    } catch (err) { showErr(err); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/uploads/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh(); flash("Avatar updated");
    } catch (err) { showErr(err); } finally { setUploading(false); }
  };

  const displayRoles = (user.roles && user.roles.length > 0) ? user.roles : [user.role];
  const avatarUrl = user.avatar ? (user.avatar.startsWith("http") ? user.avatar : `${BACKEND_URL}${user.avatar}`) : null;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2">You / Profile</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Your profile</h1>
      </div>
      <div className="p-8 lg:p-12 max-w-3xl space-y-6">
        <div className="border border-border p-8" data-testid="profile-card">
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-border">
            {avatarUrl ? (
              <img src={avatarUrl} alt={user.name} className="w-20 h-20 object-cover" data-testid="profile-avatar-image" />
            ) : (
              <div className="w-20 h-20 bg-[#0A0A0A] text-white flex items-center justify-center font-display text-3xl font-black">
                {user.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-display text-2xl font-bold">{user.name}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {displayRoles.map(rk => {
                  const rd = allRoles.find(x => x.key === rk);
                  const cls = BUILT_IN_ROLE_BG[rk];
                  const label = rd?.label || rk;
                  if (cls) {
                    return <span key={rk} className={`inline-block text-[10px] px-1.5 py-0.5 uppercase font-mono-mini ${cls}`}>{label}</span>;
                  }
                  return (
                    <span
                      key={rk}
                      className="inline-block text-[10px] px-1.5 py-0.5 uppercase font-mono-mini text-white"
                      style={{ backgroundColor: rd?.color || "#0A0A0A" }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
              <div className="mt-3">
                <label className="inline-block cursor-pointer text-xs underline text-[#002FA7]" data-testid="avatar-upload-label">
                  {uploading ? "Uploading…" : "Change photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload-input" />
                </label>
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="eyebrow">Leave balance</div>
              <div className="font-display text-3xl font-black">{user.leave_balance}</div>
            </div>
          </div>

          <form onSubmit={save} className="space-y-4" data-testid="profile-form">
            <div><label className="eyebrow block mb-2">Full name</label>
              <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="profile-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="eyebrow block mb-2">Department</label>
                <input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="profile-dept-input" /></div>
              <div><label className="eyebrow block mb-2">Position</label>
                <input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="profile-position-input" /></div>
            </div>
            <button type="submit" className="btn-primary" data-testid="save-profile">Save changes</button>
          </form>
        </div>

        <div className="border border-border p-8" data-testid="password-card">
          <div className="eyebrow mb-2">Security</div>
          <h2 className="font-display text-xl font-bold mb-4">Change password</h2>
          <form onSubmit={changePw} className="space-y-4">
            <input type="password" placeholder="Current password" required value={pw.current_password} onChange={(e) => setPw({...pw, current_password: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="current-password-input" />
            <input type="password" placeholder="New password" required value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="new-password-input" />
            <PasswordStrengthMeter value={pw.new_password} onValidityChange={setPwValid} />
            <button type="submit" disabled={!pwValid || !pw.current_password} className="btn-primary disabled:opacity-50" data-testid="change-password-button">Update password</button>
          </form>
          <div className="mt-6 pt-6 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Active sessions</div>
            <p className="text-sm text-muted-foreground mb-3">If you notice any suspicious sign-ins, sign out all other devices. Your current session will stay signed in.</p>
            <button onClick={signoutOthers} disabled={signoutBusy} className="px-4 py-2 border border-orange-500 text-orange-600 text-sm hover:bg-orange-50" data-testid="signout-others-btn">
              {signoutBusy ? "Working…" : "Sign out all other sessions"}
            </button>
          </div>
        </div>

        <div className="border border-border p-8" data-testid="email-card">
          <div className="eyebrow mb-2">Account</div>
          <h2 className="font-display text-xl font-bold mb-4">Change email</h2>
          <form onSubmit={changeEmail} className="space-y-4">
            <input type="password" placeholder="Password" required value={em.password} onChange={(e) => setEm({...em, password: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="email-password-input" />
            <input type="email" placeholder="New email" required value={em.new_email} onChange={(e) => setEm({...em, new_email: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="new-email-input" />
            <button type="submit" className="btn-primary" data-testid="change-email-button">Update email</button>
          </form>
        </div>

        {msg && <div className="border border-[#22C55E] bg-[#F0FDF4] text-[#166534] px-4 py-2 text-sm" data-testid="profile-msg">{msg}</div>}
        {error && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{error}</div>}
      </div>
    </div>
  );
}
